import { useState } from "react";
import { Plus } from "lucide-react";
import { brandUrl, useBrand } from "@/lib/brandAssets";

const ALT = "Centro Médico IEMED";
// variant → [base height for srcset (1x/2x/3x served from server), height classes]
const VARIANTS = {
  header: [64, "h-11 lg:h-16"],
  footer: [72, "h-[72px]"],
  sidebar: [56, "h-14"],
  login: [112, "h-auto max-h-24 sm:max-h-28 max-w-full"],
  compact: [40, "h-10"],
};

const Wordmark = ({ light, compact }) => (
  <span className="inline-flex items-center gap-2.5" data-testid="iemed-logo">
    <span className="relative grid h-9 w-9 place-items-center rounded-xl bg-[#1E6FD9] shadow-[0_8px_20px_-8px_rgba(30,111,217,0.8)]">
      <Plus className="h-5 w-5 text-white" strokeWidth={3} />
      <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-[#14B8A6] ring-2 ring-white" />
    </span>
    <span className="leading-none">
      <span className={`block font-display text-lg font-extrabold tracking-tight ${light ? "text-white" : "text-[#0B2545]"}`}>IEMED</span>
      {!compact && <span className={`block text-[10px] font-medium uppercase tracking-[0.22em] ${light ? "text-white/60" : "text-slate-500"}`}>Centro Médico</span>}
    </span>
  </span>
);

const LogoImage = ({ logo, variant, onError }) => {
  const [h, cls] = VARIANTS[variant];
  const set = (fmt) => [1, 2, 3].map((k) => `${brandUrl(logo[fmt][h * k])} ${k}x`).join(", ");
  return (
    <picture className={variant === "login" ? "block max-w-full" : "block shrink-0"}>
      <source type="image/webp" srcSet={set("webp")} />
      <img src={brandUrl(logo.png[h])} srcSet={set("png")} alt={ALT} width={Math.round(h * logo.aspect)} height={h}
           onError={onError} decoding="async" className={`${cls} w-auto ${variant === "login" ? "" : "max-w-none"}`} data-testid="iemed-logo-img" />
    </picture>
  );
};

const WRAP = {
  footer: "inline-flex w-fit rounded-2xl bg-white px-4 py-3 shadow-sm",
  sidebar: "flex w-full justify-center rounded-2xl bg-white px-3 py-3 shadow-sm",
  login: "inline-flex w-fit max-w-full self-center rounded-2xl bg-white px-5 py-4 shadow-sm",
};

/** Official logo when uploaded (always on white; dark surfaces get a white container), else the provisional wordmark. */
export const Logo = ({ light = false, compact = false, variant }) => {
  const brand = useBrand();
  const [failed, setFailed] = useState(false);
  const v = variant || (compact ? "compact" : "header");
  if (!brand?.has_logo || !brand.logo?.png?.[VARIANTS[v][0] * 3] || failed) return <Wordmark light={light} compact={compact} />;
  const wrap = WRAP[v] || (light ? "inline-flex w-fit rounded-2xl bg-white px-4 py-3 shadow-sm" : "inline-flex w-fit shrink-0 rounded-lg bg-white px-1.5 py-1");
  return <span className={wrap} data-testid="iemed-logo"><LogoImage logo={brand.logo} variant={v} onError={() => setFailed(true)} /></span>;
};
